import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  X,
  Check,
  Loader2,
  Sparkles,
  ShieldCheck,
  AlertCircle,
  AlertTriangle,
  RotateCcw,
  Zap,
  CheckCircle2,
} from 'lucide-react';
import { api } from '../services/api.js';

interface StarsShopModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  openInvoice: (invoiceUrl: string) => Promise<string>;
  isDemoMode?: boolean;
}

export type PaymentStage =
  | 'idle'
  | 'creating_invoice'
  | 'awaiting_payment'
  | 'confirming'
  | 'confirmed'
  | 'miner_activated'
  | 'cancelled'
  | 'failed';

export const StarsShopModal: React.FC<StarsShopModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  openInvoice,
  isDemoMode = false,
}) => {
  const [units, setUnits] = useState<number>(1);
  const [paymentStage, setPaymentStage] = useState<PaymentStage>('idle');
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState<number>(0);
  const isPollingRef = useRef<boolean>(false);

  // Reset state when opening/closing
  useEffect(() => {
    if (isOpen) {
      setPaymentStage('idle');
      setActiveOrderId(null);
      setError(null);
      setPollCount(0);
      isPollingRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const costStars = units * 10;
  const dailyYield = units * 100000;
  const presets = [1, 2, 5, 10];

  // Authoritative server polling loop
  const pollOrderStatus = async (orderId: string, maxAttempts: number = 10) => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;
    setPaymentStage('confirming');

    let attempts = 0;
    while (attempts < maxAttempts) {
      attempts++;
      setPollCount(attempts);
      try {
        const order = await api.checkStarsOrderStatus(orderId);
        if (order && order.status) {
          if (order.status === 'paid') {
            setPaymentStage('confirmed');
            isPollingRef.current = false;
            // Transition smoothly to miner_activated after celebration
            setTimeout(() => {
              setPaymentStage('miner_activated');
              onSuccess();
            }, 1200);
            return;
          } else if (order.status === 'cancelled') {
            setPaymentStage('cancelled');
            isPollingRef.current = false;
            return;
          } else if (order.status === 'failed') {
            setPaymentStage('failed');
            setError('Payment processing was declined or failed on Telegram.');
            isPollingRef.current = false;
            return;
          }
        }
      } catch (pollErr) {
        console.warn(`[Stars Status Poll Attempt ${attempts} Error]`, pollErr);
      }

      // Wait 1.5 seconds between polling checks
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    isPollingRef.current = false;
    // If still pending after all attempts
    setPaymentStage('confirming');
    setError('Payment is taking longer than usual to confirm on Telegram. Your miner will activate automatically once the payment settles.');
  };

  const handlePurchase = async () => {
    try {
      setError(null);
      setPaymentStage('creating_invoice');

      // 1. Create persistent Stars order in backend
      const invoiceRes = await api.createStarsInvoice(units);

      if (!invoiceRes.order_id || !invoiceRes.invoice_link) {
        throw new Error('Invoice link could not be generated by backend');
      }

      const orderId = invoiceRes.order_id;
      setActiveOrderId(orderId);
      setPaymentStage('awaiting_payment');

      // 2. Open genuine Telegram Stars payment dialog
      const paymentStatus = await openInvoice(invoiceRes.invoice_link);
      console.log('[Stars Payment Dialog Closed Callback]', paymentStatus);

      // Handle Telegram WebApp response callback
      if (paymentStatus === 'cancelled') {
        // Explicitly cancel order on backend
        await api.cancelStarsOrder(orderId).catch(() => {});
        setPaymentStage('cancelled');
      } else if (paymentStatus === 'failed') {
        setPaymentStage('failed');
        setError('Telegram Stars payment failed. Please check your Stars balance.');
      } else {
        // Status is 'paid', 'pending', or user closed external window:
        // NEVER trust frontend callback alone — verify authoritatively with server
        await pollOrderStatus(orderId);
      }
    } catch (err: unknown) {
      console.error('[Stars Purchase Failed]', err);
      setError(err instanceof Error ? err.message : 'Failed to initiate Stars checkout');
      setPaymentStage('failed');
    }
  };

  const handleManualCheck = () => {
    if (activeOrderId) {
      pollOrderStatus(activeOrderId, 6);
    }
  };

  const handleCancelOrder = async () => {
    if (activeOrderId) {
      await api.cancelStarsOrder(activeOrderId).catch(() => {});
    }
    setPaymentStage('cancelled');
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="w-full max-w-md bg-space-900 border border-nova-gold/30 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl relative overflow-hidden"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-space-800 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* ================= STAGE: IDLE ================= */}
          {paymentStage === 'idle' && (
            <div>
              {/* Modal Header */}
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-nova-gold/40 flex items-center justify-center text-nova-gold shadow-lg shadow-amber-500/20">
                  <Star className="w-7 h-7 fill-nova-gold text-nova-gold" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-white flex items-center space-x-1.5">
                    <span>Deploy Stars Miners</span>
                    <Sparkles className="w-4 h-4 text-nova-gold" />
                  </h3>
                  <p className="text-xs text-gray-400">
                    10 Telegram Stars = 100,000 NOVA / 24h
                  </p>
                </div>
              </div>

              {/* Preset Buttons */}
              <div className="mt-5 grid grid-cols-4 gap-2">
                {presets.map((p) => (
                  <button
                    key={p}
                    onClick={() => setUnits(p)}
                    className={`py-2.5 rounded-xl text-xs font-bold transition-all border ${
                      units === p
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-600 text-black border-amber-400 shadow-md shadow-amber-500/30'
                        : 'bg-space-800 text-gray-300 border-white/5 hover:border-white/20'
                    }`}
                  >
                    {p}x Rig{p > 1 ? 's' : ''}
                  </button>
                ))}
              </div>

              {/* Yield Calculation Summary */}
              <div className="mt-5 rounded-2xl bg-space-950/70 border border-white/10 p-4 space-y-2.5">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-400">Deploy Quantity:</span>
                  <span className="font-bold text-white">{units} Miner Unit{units > 1 ? 's' : ''}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-400">Total 24h Yield:</span>
                  <span className="font-mono font-bold text-nova-cyan">
                    +{dailyYield.toLocaleString()} NOVA / day
                  </span>
                </div>
                <div className="pt-2 border-t border-white/5 flex justify-between items-center text-sm font-bold">
                  <span className="text-gray-300">Stars Checkout:</span>
                  <span className="text-nova-gold flex items-center space-x-1">
                    <Star className="w-4 h-4 fill-nova-gold inline" />
                    <span>{costStars} Stars (XTR)</span>
                  </span>
                </div>
              </div>

              {/* Security guarantee */}
              <div className="mt-3 flex items-center space-x-1.5 text-[11px] text-gray-400 px-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                <span>Instant deployment • Unlimited concurrent purchases • Anti-duplicate protected</span>
              </div>

              {isDemoMode && (
                <div className="mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-nova-gold text-xs font-medium">
                  <span className="font-bold uppercase tracking-wider block mb-0.5">Demo Sandbox Mode</span>
                  Real payment processing is disabled. Deploy the Cloudflare Worker API with a live Telegram Bot Token to process production Stars payments.
                </div>
              )}

              {/* Buy Button */}
              <button
                id="btn-confirm-stars-purchase"
                onClick={() => {
                  if (isDemoMode) {
                    alert('DEMO MODE: Real Telegram Stars invoices require live bot configuration. Connect your Cloudflare Worker to enable production checkout.');
                    return;
                  }
                  handlePurchase();
                }}
                className={`mt-5 w-full py-3.5 rounded-2xl font-black text-sm flex items-center justify-center space-x-2 shadow-xl transition-all ${
                  isDemoMode
                    ? 'bg-space-850 text-gray-400 border border-white/10 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black shadow-amber-500/25 hover:brightness-110 active:scale-[0.98]'
                }`}
              >
                <Star className="w-4 h-4 fill-black" />
                <span>Pay {costStars} Telegram Stars</span>
              </button>
            </div>
          )}

          {/* ================= STAGE: CREATING INVOICE ================= */}
          {paymentStage === 'creating_invoice' && (
            <div className="py-8 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-nova-gold/40 flex items-center justify-center text-nova-gold mb-4 relative">
                <Loader2 className="w-8 h-8 animate-spin text-nova-gold" />
                <Star className="w-4 h-4 fill-nova-gold absolute" />
              </div>
              <h3 className="text-lg font-black text-white">Creating Invoice</h3>
              <p className="text-xs text-gray-400 mt-2 max-w-xs">
                Generating persistent order record and authenticating with Telegram Stars Bot API...
              </p>
              <div className="mt-6 inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-space-800 text-[11px] font-mono text-nova-gold border border-nova-gold/20">
                <span>Currency: XTR • Amount: {costStars} Stars</span>
              </div>
            </div>
          )}

          {/* ================= STAGE: AWAITING PAYMENT ================= */}
          {paymentStage === 'awaiting_payment' && (
            <div className="py-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-400/30 flex items-center justify-center text-nova-gold mb-4">
                <Star className="w-8 h-8 fill-nova-gold animate-pulse text-nova-gold" />
              </div>
              <h3 className="text-lg font-black text-white">Awaiting Payment</h3>
              <p className="text-xs text-gray-400 mt-1 max-w-xs">
                Please complete the payment in the Telegram Stars dialog.
              </p>

              <div className="mt-4 w-full rounded-2xl bg-space-950/70 border border-white/10 p-3.5 space-y-1.5 text-xs text-left">
                <div className="flex justify-between">
                  <span className="text-gray-400">Order ID:</span>
                  <span className="font-mono text-gray-300 truncate max-w-[180px]">{activeOrderId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Stars Amount:</span>
                  <span className="font-bold text-nova-gold">{costStars} Stars (XTR)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Miners:</span>
                  <span className="font-bold text-white">{units}x Stars Miner</span>
                </div>
              </div>

              <div className="mt-6 flex w-full space-x-2">
                <button
                  onClick={handleCancelOrder}
                  className="flex-1 py-3 rounded-xl bg-space-800 border border-white/10 text-xs font-bold text-gray-400 hover:text-white transition-colors"
                >
                  Cancel Order
                </button>
                <button
                  onClick={handleManualCheck}
                  className="flex-1 py-3 rounded-xl bg-amber-500/20 border border-amber-500/40 text-xs font-bold text-nova-gold hover:bg-amber-500/30 transition-colors flex items-center justify-center space-x-1"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Check Status</span>
                </button>
              </div>
            </div>
          )}

          {/* ================= STAGE: CONFIRMING ================= */}
          {paymentStage === 'confirming' && (
            <div className="py-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 border border-nova-cyan/40 flex items-center justify-center text-nova-cyan mb-4">
                <Loader2 className="w-8 h-8 animate-spin text-nova-cyan" />
              </div>
              <h3 className="text-lg font-black text-white">Verifying Transaction</h3>
              <p className="text-xs text-gray-400 mt-1 max-w-xs">
                Authoritatively verifying Telegram Stars payment and webhook delivery with Nova backend...
              </p>

              {pollCount > 0 && (
                <p className="text-[11px] font-mono text-nova-cyan/80 mt-3">
                  Verification check #{pollCount}...
                </p>
              )}

              {error && (
                <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs text-left">
                  {error}
                </div>
              )}

              <div className="mt-6 flex w-full space-x-2">
                <button
                  onClick={handleManualCheck}
                  className="flex-1 py-3 rounded-xl bg-space-800 border border-white/10 text-xs font-bold text-white hover:bg-space-700 transition-colors flex items-center justify-center space-x-1"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Retry Check</span>
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl bg-space-850 border border-white/5 text-xs font-bold text-gray-400 hover:text-white transition-colors"
                >
                  Close & Wait
                </button>
              </div>
            </div>
          )}

          {/* ================= STAGE: CONFIRMED ================= */}
          {paymentStage === 'confirmed' && (
            <div className="py-8 flex flex-col items-center text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400 mb-4 shadow-lg shadow-emerald-500/20"
              >
                <CheckCircle2 className="w-9 h-9 text-emerald-400" />
              </motion.div>
              <h3 className="text-lg font-black text-white">Payment Confirmed</h3>
              <p className="text-xs text-gray-300 mt-1 max-w-xs">
                Telegram Stars payment verified by backend webhook! Activating Stars Miner...
              </p>
            </div>
          )}

          {/* ================= STAGE: MINER ACTIVATED ================= */}
          {paymentStage === 'miner_activated' && (
            <div className="py-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-black mb-4 shadow-xl shadow-amber-500/30">
                <Zap className="w-8 h-8 fill-black text-black" />
              </div>
              <h3 className="text-lg font-black text-white flex items-center space-x-1.5">
                <span>Miner Activated!</span>
                <Sparkles className="w-4 h-4 text-nova-gold" />
              </h3>
              <p className="text-xs text-gray-300 mt-1 max-w-xs">
                Successfully deployed {units} Stars Miner rig{units > 1 ? 's' : ''}! Yield generation is now live.
              </p>

              <div className="mt-4 w-full rounded-2xl bg-space-950/70 border border-emerald-500/30 p-4 space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Deployed Rigs:</span>
                  <span className="font-bold text-white">{units}x Stars Miner</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Mining Hashrate:</span>
                  <span className="font-mono font-bold text-emerald-400">
                    +{dailyYield.toLocaleString()} NOVA / 24h
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Status:</span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">
                    ONLINE & MINING
                  </span>
                </div>
              </div>

              <button
                id="btn-miner-activated-done"
                onClick={onClose}
                className="mt-6 w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-black text-sm shadow-xl shadow-amber-500/20 hover:brightness-110 active:scale-[0.98] transition-all"
              >
                Start Mining
              </button>
            </div>
          )}

          {/* ================= STAGE: CANCELLED ================= */}
          {paymentStage === 'cancelled' && (
            <div className="py-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-4">
                <AlertCircle className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-black text-white">Payment Cancelled</h3>
              <p className="text-xs text-gray-400 mt-1 max-w-xs">
                The Telegram Stars invoice was cancelled. No Stars were charged to your account.
              </p>

              <div className="mt-6 flex w-full space-x-2">
                <button
                  onClick={() => setPaymentStage('idle')}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 text-black text-xs font-black hover:brightness-110 transition-all"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl bg-space-800 border border-white/10 text-xs font-bold text-gray-400 hover:text-white transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* ================= STAGE: FAILED ================= */}
          {paymentStage === 'failed' && (
            <div className="py-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-4">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-black text-white">Payment Failed</h3>
              <p className="text-xs text-gray-400 mt-1 max-w-xs">
                {error || 'The Stars checkout could not be completed. Please ensure you have sufficient Telegram Stars.'}
              </p>

              <div className="mt-6 flex w-full space-x-2">
                <button
                  onClick={() => setPaymentStage('idle')}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 text-black text-xs font-black hover:brightness-110 transition-all"
                >
                  Retry Payment
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl bg-space-800 border border-white/10 text-xs font-bold text-gray-400 hover:text-white transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

